import React, { useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import Chatbot from '@/components/shared/Chatbot';
import CourseCardWithMode from '@/components/courses/CourseCardWithMode';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '@/App';
import { toast } from 'sonner';
import { 
  Search, GraduationCap, Clock, IndianRupee, Award, Briefcase, 
  CheckCircle, Play, ChevronRight, Users, BookOpen, Monitor,
  Calculator, Brain, Building2, Phone, MessageCircle, Star,
  Eye, TrendingUp, Shield, Sparkles, ArrowRight, Globe, MapPin
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CoursesPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useContext(AuthContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [enrollingCourse, setEnrollingCourse] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'professional', 'distance'

  // Handle Enroll Now click with STT/RPL mode
  const handleEnrollClick = (course, mode = null, calculatedFee = null) => {
    if (isAuthenticated && user) {
      // User is logged in - proceed to enrollment
      if (user.role === 'student') {
        // Check if mode was selected (for CourseCardWithMode)
        if (mode) {
          // Store the mode and fee in sessionStorage for AdmissionPage
          sessionStorage.setItem('selectedCourseMode', JSON.stringify({
            course_id: course.id,
            course_name: course.title || course.name,
            original_fee: course.fee,
            training_mode: mode,
            calculated_fee: calculatedFee
          }));
          toast.success(`Enrolling in ${course.title || course.name} (${mode} Mode)...`);
        } else {
          toast.success(`Enrolling in ${course.title || course.name}...`);
        }
        // Navigate to student dashboard courses tab
        navigate('/student/admission');
      } else {
        toast.error('Only students can enroll in courses');
      }
    } else {
      // User not logged in - redirect to login with return URL
      toast.info('Please login to enroll in courses');
      navigate('/auth/login?redirect=/courses');
    }
  };

  // ============================================================
  // DYNAMIC COURSES: Fetch from Admin-managed DB via /api/courses
  // Any change in Admin Dashboard → auto-reflects on next fetch.
  // Also auto-refreshes when the browser tab regains focus.
  // ============================================================
  const [allCourses, setAllCourses] = useState([]);
  const [degreePrograms, setDegreePrograms] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);

  // Image + career defaults for enrichment
  const CATEGORY_IMAGE_DEFAULTS = {
    technical: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400',
    'it-digital': 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400',
    accounting: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400',
    advanced: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400',
    vocational: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=400'
  };
  const NAME_IMAGE_MAP = {
    'autocad': 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=400',
    'civil cad': 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400',
    '3ds max': 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=400',
    'interior architecture': 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400',
    'land surveyor': 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400',
    'digital marketing': 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400',
    'web designer': 'https://images.unsplash.com/photo-1547658719-da2b51169166?w=400',
    'data entry operator': 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=400',
    'tally with gst': 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400',
    'financial accounting': 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400',
    'ai data science': 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400',
    'ai data management': 'https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=400',
    'safety management': 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400'
  };
  const POPULAR_KEYWORDS = ['autocad', 'digital marketing', 'tally', 'ai data science'];
  const NAME_CAREER_MAP = {
    'autocad': { career: 'CAD Designer', salary: '₹18,000 - ₹45,000/month' },
    'digital marketing': { career: 'Digital Marketing Executive', salary: '₹15,000 - ₹50,000/month' },
    'tally with gst': { career: 'Accountant / GST Specialist', salary: '₹15,000 - ₹35,000/month' },
    'ai data science': { career: 'Data Scientist / ML Engineer', salary: '₹30,000 - ₹80,000/month' },
    'web designer': { career: 'Web Designer / Developer', salary: '₹18,000 - ₹45,000/month' },
    'interior architecture': { career: 'Interior Designer', salary: '₹25,000 - ₹60,000/month' },
    'civil cad': { career: 'Civil Designer', salary: '₹20,000 - ₹50,000/month' },
    'land surveyor': { career: 'Land Surveyor', salary: '₹18,000 - ₹40,000/month' },
    '3ds max': { career: '3D Artist / Animator', salary: '₹22,000 - ₹55,000/month' },
    'data entry operator': { career: 'Data Entry Operator', salary: '₹12,000 - ₹25,000/month' },
    'financial accounting': { career: 'Finance Executive', salary: '₹18,000 - ₹45,000/month' },
    'ai data management': { career: 'Data Analyst / DB Admin', salary: '₹25,000 - ₹60,000/month' },
    'safety management': { career: 'Safety Officer', salary: '₹20,000 - ₹50,000/month' }
  };

  const classifyUiCategory = (name) => {
    const n = (name || '').toLowerCase();
    if (/cad|3ds max|interior|surveyor|drafting/.test(n)) return 'technical';
    if (/digital|web|data entry|seo/.test(n)) return 'it-digital';
    if (/tally|account|gst|finance/.test(n)) return 'accounting';
    if (/ai |data science|machine|safety|management/.test(n)) return 'advanced';
    return 'technical';
  };

  const extractYears = (duration) => {
    if (!duration && duration !== 0) return 1;
    const s = String(duration).trim();
    if (/^\d+$/.test(s)) return parseInt(s, 10);
    const y = s.match(/(\d+)\s*Year/i);
    if (y) return parseInt(y[1], 10);
    const m = s.match(/(\d+)\s*Month/i);
    if (m) return parseInt(m[1], 10) >= 12 ? Math.floor(parseInt(m[1], 10) / 12) : 1;
    return 1;
  };

  const toProfessionalCard = (c) => {
    const nameKey = (c.name || '').toLowerCase();
    // Longest-key match first so "3ds max design" also matches "3ds max"
    const matchedKey = Object.keys(NAME_CAREER_MAP).find(k => nameKey.includes(k));
    const career = matchedKey ? NAME_CAREER_MAP[matchedKey] : { career: 'Industry Professional', salary: '₹15,000 - ₹50,000/month' };
    const imgKey = Object.keys(NAME_IMAGE_MAP).find(k => nameKey.includes(k));
    const uiCat = classifyUiCategory(c.name);
    return {
      id: c.id,
      title: c.name,
      category: uiCat,
      duration: c.duration || '—',
      fee: c.fee || 0,
      description: c.description || '',
      image: (imgKey && NAME_IMAGE_MAP[imgKey]) || CATEGORY_IMAGE_DEFAULTS[uiCat] || CATEGORY_IMAGE_DEFAULTS.technical,
      badges: c.course_type === 'govt' ? ['Certificate', 'Govt Approved'] : ['Certificate', 'Job Assistance'],
      popular: POPULAR_KEYWORDS.some(k => nameKey.includes(k)),
      syllabus: Array.isArray(c.syllabus) ? c.syllabus : [],
      career: career.career,
      salary: career.salary,
      tools: []
    };
  };

  const toDegreeCard = (c) => {
    const years = extractYears(c.duration);
    const rawDur = String(c.duration || '').trim();
    const normalizedDuration = /^\d+$/.test(rawDur)
      ? `${rawDur} ${parseInt(rawDur, 10) === 1 ? 'Year' : 'Years'}`
      : (rawDur || `${years} ${years === 1 ? 'Year' : 'Years'}`);
    let uiCat = 'UG';
    let color = 'blue';
    if (c.category === 'diploma' || /diploma|pgdca/i.test(c.name)) { uiCat = 'Diploma'; color = 'purple'; }
    else if (years === 2 || /^m[.\s]|master|mba|mca|m\.com|m\.sc/i.test(c.name)) { uiCat = 'PG'; color = 'emerald'; }
    else { uiCat = 'UG'; color = 'blue'; }
    const yearlyFee = c.yearly_fee != null ? c.yearly_fee : (c.fee || 0);
    const feeType = c.fee_type === 'total' || uiCat === 'Diploma' ? 'Total' : 'Yearly';
    const totalFee = c.total_fee != null
      ? c.total_fee
      : (feeType === 'Yearly' ? yearlyFee * years : yearlyFee);
    return {
      id: c.id, title: c.name, duration: normalizedDuration,
      yearlyFee, feeType, description: c.description || '',
      category: uiCat, color, fee: yearlyFee, totalFee, durationYears: years
    };
  };

  const loadCourses = useCallback(async () => {
    try {
      // Cache-busting query param + backend also sets Cache-Control: no-store
      const res = await axios.get(`${API}/courses`, { params: { _t: Date.now() } });
      const data = Array.isArray(res.data) ? res.data : [];
      const professionals = [];
      const degrees = [];
      for (const c of data) {
        if (!c || c.is_active === false) continue;
        const cat = (c.category || '').toLowerCase();
        if (cat === 'degree' || cat === 'distance' || cat === 'diploma' || cat === 'distance_learning') {
          degrees.push(toDegreeCard(c));
        } else {
          professionals.push(toProfessionalCard(c));
        }
      }
      const degreeOrder = { Diploma: 0, UG: 1, PG: 2 };
      degrees.sort((a, b) => (degreeOrder[a.category] - degreeOrder[b.category]));
      setAllCourses(professionals);
      setDegreePrograms(degrees);
    } catch (err) {
      console.error('Failed to load courses from API', err);
      setAllCourses([]);
      setDegreePrograms([]);
    } finally {
      setCoursesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCourses();
    // Auto-refresh when user returns to the tab (admin updates propagate live)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadCourses();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [loadCourses]);

  // Filter courses by search query
  const filteredCourses = allCourses.filter(course => {
    if (!searchQuery) return true;
    return course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
           course.description.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const popularCourses = allCourses.filter(c => c.popular);

  // Career Opportunities Data
  const careerOpportunities = [
    { course: 'AutoCAD', role: 'CAD Designer / Draftsman', salary: '₹18,000 - ₹45,000/month', icon: Monitor },
    { course: 'Digital Marketing', role: 'Marketing Executive', salary: '₹15,000 - ₹50,000/month', icon: TrendingUp },
    { course: 'Tally with GST', role: 'Accountant', salary: '₹15,000 - ₹35,000/month', icon: Calculator },
    { course: 'AI Data Science', role: 'Data Scientist', salary: '₹30,000 - ₹80,000/month', icon: Brain },
    { course: 'Web Designer', role: 'Front-End Developer', salary: '₹18,000 - ₹45,000/month', icon: BookOpen },
    { course: 'Interior Architecture', role: 'Interior Designer', salary: '₹25,000 - ₹60,000/month', icon: Building2 }
  ];

  // Why Choose Us
  const whyChooseUs = [
    { icon: Users, title: 'Industry Trainers', description: 'Learn from experienced professionals with 10+ years industry experience' },
    { icon: Award, title: 'Govt Certification', description: 'Get recognized certificates affiliated with Skill India and NSDC' },
    { icon: Briefcase, title: 'Placement Support', description: '70%+ placement rate with dedicated career assistance' },
    { icon: Monitor, title: 'Practical Training', description: 'Hands-on projects and real-world case studies' }
  ];

  // Course Card Component
  const CourseCard = ({ course, featured = false }) => (
    <div 
      className={`group bg-white rounded-2xl border border-slate-200 overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-2 ${featured ? 'ring-2 ring-blue-500' : ''}`}
      data-testid={`course-card-${course.id}`}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img 
          src={course.image} 
          alt={course.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        {course.popular && (
          <span className="absolute top-3 left-3 bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
            <Star className="w-3 h-3" /> Most Popular
          </span>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute bottom-3 left-3 right-3 flex gap-2">
          {course.badges.slice(0, 2).map((badge, i) => (
            <span key={i} className="bg-white/90 text-slate-700 px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
              <CheckCircle className="w-3 h-3 text-emerald-500" /> {badge}
            </span>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
          {course.title}
        </h3>
        <p className="text-slate-600 text-sm mb-4 line-clamp-2">{course.description}</p>

        {/* Meta */}
        <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" /> {course.duration}
          </span>
          <span className="flex items-center gap-1">
            <IndianRupee className="w-4 h-4" /> {course.fee.toLocaleString()}
          </span>
        </div>

        {/* Buttons */}
        <div className="flex gap-2">
          <Button 
            onClick={() => handleEnrollClick(course)} 
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            <GraduationCap className="w-4 h-4 mr-1" /> Enroll Now
          </Button>
          <Button 
            variant="outline"
            className="flex-1 border-slate-300 hover:bg-slate-50"
            onClick={() => {/* Could open modal with details */}}
          >
            <Eye className="w-4 h-4 mr-1" /> Details
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* HERO SECTION */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 py-16 lg:py-24" data-testid="courses-hero">
        {/* Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Upgrade Your Skills &<br />
              <span className="text-blue-200">Get Job Ready</span>
            </h1>
            <p className="text-lg sm:text-xl text-blue-100 max-w-2xl mx-auto">
              Choose from industry-relevant courses designed for real-world careers
            </p>
          </div>

          {/* Search Box */}
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl p-4 sm:p-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search courses (AutoCAD, Digital Marketing, AI...)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-4 py-6 text-lg border-slate-200 rounded-xl"
                  data-testid="course-search-input"
                />
              </div>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <button
              onClick={() => setActiveTab('all')}
              data-testid="tab-all-courses"
              className={`px-6 py-3 rounded-full font-semibold transition-all ${
                activeTab === 'all'
                  ? 'bg-white text-blue-600 shadow-lg'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              All Courses
            </button>
            <button
              onClick={() => setActiveTab('professional')}
              data-testid="tab-professional"
              className={`px-6 py-3 rounded-full font-semibold transition-all flex items-center gap-2 ${
                activeTab === 'professional'
                  ? 'bg-white text-orange-600 shadow-lg'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              <Briefcase className="w-4 h-4" /> Professional Courses
            </button>
            <button
              onClick={() => setActiveTab('distance')}
              data-testid="tab-distance-education"
              className={`px-6 py-3 rounded-full font-semibold transition-all flex items-center gap-2 ${
                activeTab === 'distance'
                  ? 'bg-white text-purple-600 shadow-lg'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              <Globe className="w-4 h-4" /> Distance Education
            </button>
          </div>
        </div>
      </section>

      {/* DISTANCE EDUCATION HERO SECTION - Show when distance tab active */}
      {activeTab === 'distance' && (
        <section className="py-16 bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600" data-testid="distance-education-hero">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur rounded-full text-sm font-semibold mb-6">
                  <Globe className="w-4 h-4" /> UGC-DEB Approved Universities
                </span>
                <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Distance Education
                  <span className="block text-purple-200">Learn from Anywhere</span>
                </h2>
                <p className="text-lg text-purple-100 mb-8 leading-relaxed">
                  Get UGC recognized Degrees and Diplomas through our affiliated universities. 
                  Study at your own pace with flexible schedules and online support.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                    <GraduationCap className="w-8 h-8 text-yellow-300 mb-2" />
                    <p className="font-bold text-xl">9+ Programs</p>
                    <p className="text-purple-200 text-sm">Diploma, UG & PG</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                    <Award className="w-8 h-8 text-yellow-300 mb-2" />
                    <p className="font-bold text-xl">Valid for Govt Jobs</p>
                    <p className="text-purple-200 text-sm">UGC Recognized</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                    <BookOpen className="w-8 h-8 text-yellow-300 mb-2" />
                    <p className="font-bold text-xl">Online Study</p>
                    <p className="text-purple-200 text-sm">Material & Support</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                    <Clock className="w-8 h-8 text-yellow-300 mb-2" />
                    <p className="font-bold text-xl">Flexible Schedule</p>
                    <p className="text-purple-200 text-sm">Study at Your Pace</p>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="bg-white rounded-3xl shadow-2xl p-8">
                  <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                    <GraduationCap className="w-6 h-6 text-purple-600" /> Available Programs
                  </h3>
                  <div className="space-y-3">
                    {degreePrograms.map(program => (
                      <div 
                        key={program.id}
                        className={`flex items-center justify-between p-3 rounded-xl border-2 transition-all hover:shadow-md cursor-pointer ${
                          program.category === 'Diploma' ? 'border-purple-200 hover:border-purple-400 bg-purple-50' :
                          program.category === 'UG' ? 'border-blue-200 hover:border-blue-400 bg-blue-50' :
                          'border-emerald-200 hover:border-emerald-400 bg-emerald-50'
                        }`}
                        onClick={() => handleEnrollClick({title: program.title, ...program})}
                      >
                        <div>
                          <p className="font-bold text-slate-800">{program.title}</p>
                          <p className="text-xs text-slate-500">{program.duration} • {program.category}</p>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${
                            program.category === 'Diploma' ? 'text-purple-600' :
                            program.category === 'UG' ? 'text-blue-600' : 'text-emerald-600'
                          }`}>₹{program.fee.toLocaleString()}</p>
                          <p className="text-xs text-slate-500">{program.feeType}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* POPULAR COURSES - Show for 'all' and 'professional' tabs */}
      {(activeTab === 'all' || activeTab === 'professional') && (
      <section className="py-16 bg-gradient-to-b from-slate-50 to-white" data-testid="popular-courses">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold mb-3">
                <Star className="w-4 h-4" /> Most Popular
              </span>
              <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Popular Courses
              </h2>
            </div>
            <Button variant="ghost" className="hidden sm:flex gap-2" onClick={() => setSelectedCategory('all')}>
              View All <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {popularCourses.map(course => (
              <CourseCardWithMode 
                key={course.id} 
                course={course} 
                featured 
                onEnroll={handleEnrollClick}
                onViewDetails={(c) => {/* Could open modal with details */}}
              />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ALL COURSES - Show for 'all' and 'professional' tabs */}
      {(activeTab === 'all' || activeTab === 'professional') && (
      <section className="py-16 bg-white" data-testid="all-courses">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              All Professional Courses
            </h2>
            <p className="text-lg text-slate-600">
              {filteredCourses.length} courses available
            </p>
          </div>

          {filteredCourses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredCourses.map(course => (
                <CourseCardWithMode 
                  key={course.id} 
                  course={course}
                  onEnroll={handleEnrollClick}
                  onViewDetails={(c) => {/* Could open modal with details */}}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-slate-50 rounded-2xl">
              <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-2">No courses found</p>
              <p className="text-sm text-slate-500 mb-4">Try a different search term</p>
              <Button 
                variant="outline" 
                onClick={() => setSearchQuery('')}
                className="mt-2"
              >
                Clear Search
              </Button>
            </div>
          )}
        </div>
      </section>
      )}

      {/* DEGREE PROGRAMS - Show for 'all' tab only */}
      {activeTab === 'all' && (
      <section className="py-16 bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50" data-testid="degree-programs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold mb-3">
              <GraduationCap className="w-4 h-4" /> UGC Recognized
            </span>
            <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Degree Programs
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Distance learning degree programs in collaboration with leading universities
            </p>
            <Button 
              variant="outline"
              onClick={() => setActiveTab('distance')}
              className="mt-4 border-purple-300 text-purple-600 hover:bg-purple-50"
            >
              <Globe className="w-4 h-4 mr-2" /> View Distance Education Section
            </Button>
          </div>

          {/* Diploma */}
          <div className="mb-10">
            <h3 className="text-lg font-bold text-purple-700 mb-4 flex items-center gap-2">
              <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm">Diploma</span>
              Post Graduate Diploma
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {degreePrograms.filter(d => d.category === 'Diploma').map(degree => (
                <div key={degree.id} className="bg-white rounded-xl border-2 border-purple-200 p-5 hover:shadow-lg transition-all">
                  <h4 className="text-xl font-bold text-slate-900 mb-2">{degree.title}</h4>
                  <p className="text-sm text-slate-600 mb-3">{degree.description}</p>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-slate-500 text-sm flex items-center gap-1">
                      <Clock className="w-4 h-4" /> {degree.duration}
                    </span>
                    <div className="text-right">
                      <p className="text-xl font-bold text-purple-600">₹{degree.yearlyFee.toLocaleString()}</p>
                      <p className="text-xs text-slate-500">{degree.feeType === 'Yearly' ? '/Year' : 'Total Fee'}</p>
                    </div>
                  </div>
                  {degree.feeType === 'Yearly' && degree.totalFee > degree.yearlyFee && (
                    <div className="bg-purple-50 rounded-lg p-2 mb-3 text-center">
                      <span className="text-xs text-purple-700">Total Course Fee: </span>
                      <span className="text-sm font-bold text-purple-800">₹{degree.totalFee.toLocaleString()}</span>
                    </div>
                  )}
                  <Button onClick={() => handleEnrollClick({title: degree.title, ...degree})} className="w-full bg-purple-600 hover:bg-purple-700">
                    Enroll Now
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* UG Degrees */}
          <div className="mb-10">
            <h3 className="text-lg font-bold text-blue-700 mb-4 flex items-center gap-2">
              <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">UG</span>
              Undergraduate Programs
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {degreePrograms.filter(d => d.category === 'UG').map(degree => (
                <div key={degree.id} className="bg-white rounded-xl border-2 border-blue-200 p-5 hover:shadow-lg transition-all">
                  <h4 className="text-xl font-bold text-slate-900 mb-2">{degree.title}</h4>
                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">{degree.description}</p>
                  <div className="mb-2">
                    <p className="text-xl font-bold text-blue-600">₹{degree.yearlyFee.toLocaleString()}<span className="text-sm font-normal text-slate-500">/Year</span></p>
                    <p className="text-xs text-slate-500">{degree.duration}</p>
                  </div>
                  {degree.totalFee > degree.yearlyFee && (
                    <div className="bg-blue-50 rounded-lg p-2 mb-3 text-center">
                      <span className="text-xs text-blue-700">Total: </span>
                      <span className="text-sm font-bold text-blue-800">₹{degree.totalFee.toLocaleString()}</span>
                    </div>
                  )}
                  <Button onClick={() => handleEnrollClick({title: degree.title, ...degree})} className="w-full bg-blue-600 hover:bg-blue-700" size="sm">
                    Enroll Now
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* PG Degrees */}
          <div>
            <h3 className="text-lg font-bold text-emerald-700 mb-4 flex items-center gap-2">
              <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm">PG</span>
              Postgraduate Programs
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {degreePrograms.filter(d => d.category === 'PG').map(degree => (
                <div key={degree.id} className="bg-white rounded-xl border-2 border-emerald-200 p-5 hover:shadow-lg transition-all">
                  <h4 className="text-xl font-bold text-slate-900 mb-2">{degree.title}</h4>
                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">{degree.description}</p>
                  <div className="mb-2">
                    <p className="text-xl font-bold text-emerald-600">₹{degree.yearlyFee.toLocaleString()}<span className="text-sm font-normal text-slate-500">/Year</span></p>
                    <p className="text-xs text-slate-500">{degree.duration}</p>
                  </div>
                  {degree.totalFee > degree.yearlyFee && (
                    <div className="bg-emerald-50 rounded-lg p-2 mb-3 text-center">
                      <span className="text-xs text-emerald-700">Total: </span>
                      <span className="text-sm font-bold text-emerald-800">₹{degree.totalFee.toLocaleString()}</span>
                    </div>
                  )}
                  <Button onClick={() => handleEnrollClick({title: degree.title, ...degree})} className="w-full bg-emerald-600 hover:bg-emerald-700" size="sm">
                    Enroll Now
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* VIDEO DEMO SECTION - Always show */}
      <section className="py-16 bg-slate-50" data-testid="course-demos">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold mb-3">
              <Play className="w-4 h-4" /> Watch & Learn
            </span>
            <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Course Demo Videos
            </h2>
            <p className="text-lg text-slate-600">See how you will learn with our training methodology</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: 'AutoCAD Training Demo', desc: 'Learn 2D & 3D drafting techniques', duration: '3 Months', fee: 12500, image: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=600' },
              { title: 'Digital Marketing Demo', desc: 'Master SEO, social media, content marketing', duration: '2 Months', fee: 12000, image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600' },
              { title: 'AI Data Science Demo', desc: 'Python, ML, real-world projects', duration: '6 Months', fee: 40000, image: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=600' }
            ].map((video, index) => (
              <div key={index} className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl transition-all group">
                <div className="relative aspect-video">
                  <img src={video.image} alt={video.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/50 transition-colors">
                    <button className="w-16 h-16 bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform shadow-lg">
                      <Play className="w-8 h-8 text-blue-600 ml-1" fill="currentColor" />
                    </button>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{video.title}</h3>
                  <p className="text-slate-600 text-sm mb-4">{video.desc}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500 text-sm">{video.duration}</span>
                    <span className="font-bold text-blue-600">₹{video.fee.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-16 bg-white" data-testid="why-choose-us">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold mb-3">
              <Shield className="w-4 h-4" /> Why Iqra Skills
            </span>
            <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Why Choose Us
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-6 border border-slate-100 hover:shadow-lg transition-all text-center group">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAREER OPPORTUNITIES */}
      <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="career-opportunities">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm font-semibold mb-3">
              <Briefcase className="w-4 h-4" /> Career Growth
            </span>
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Career Opportunities
            </h2>
            <p className="text-lg text-slate-400">Transform your skills into high-paying careers</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {careerOpportunities.map((career, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-all">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <career.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">{career.course}</h3>
                    <p className="text-slate-400 text-sm">{career.role}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-4 border-t border-white/10">
                  <span className="text-slate-400 text-sm">Salary Range</span>
                  <span className="text-emerald-400 font-bold">{career.salary}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAREER COUNSELOR CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 relative overflow-hidden" data-testid="career-counselor-cta">
        {/* Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Not Sure Which Course is Right for You?
          </h2>
          <p className="text-xl text-blue-100 mb-10">
            Talk to our career counselors and find the perfect course for your goals
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+919521484003">
              <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50 font-bold px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all w-full sm:w-auto">
                <Phone className="w-5 h-5 mr-2" />
                Call Now: 9521484003
              </Button>
            </a>
            <a href="https://wa.me/919521484003?text=Hi, I need help choosing the right course" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all w-full sm:w-auto">
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp Us
              </Button>
            </a>
          </div>

          <p className="text-blue-200 mt-8 text-sm">
            Free career counseling • No obligations • Expert guidance
          </p>
        </div>
      </section>

      <Footer />
      <Chatbot />
    </div>
  );
};

export default CoursesPage;
